import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import type { FAQ } from '../types';
import { Button } from '../../../components/ui/Button';


const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState<string | null>("1");

  const faqs: FAQ[] = [
    {
      id: "1",
      question: "How long does it take to implement an AI chatbot?",
      answer: "Most implementations are completed within 2-4 weeks, depending on complexity. Our process includes: initial consultation (1-2 days), AI training and customization (1 week), integration with your systems (3-5 days), testing and refinement (3-5 days), and team training (1-2 days). We provide a detailed timeline during the free audit."
    },
    {
      id: "2",
      question: "Can the chatbot integrate with our existing CRM and helpdesk?",
      answer: "Yes! Our chatbots integrate seamlessly with popular platforms like Salesforce, HubSpot, Zendesk, Freshdesk, and many others. We also offer custom API integrations for proprietary systems. All customer data, conversation history, and tickets are automatically synced in real-time."
    },
    {
      id: "3",
      question: "What happens when the chatbot can't answer a question?",
      answer: "Our intelligent escalation system smoothly transfers complex queries to human agents with full conversation context. You can set custom rules for when to escalate based on keywords, sentiment, or customer value. The handoff is seamless, and agents receive all previous conversation history."
    },
    {
      id: "4",
      question: "How do you ensure the chatbot understands our specific business?",
      answer: "We train your chatbot using your knowledge base, FAQs, product documentation, and past customer interactions. The AI learns your brand voice, terminology, and policies. We also implement continuous learning, where the bot improves from every interaction and regular feedback from your team."
    },
    {
      id: "5",
      question: "Is customer data secure and compliant with regulations?",
      answer: "Absolutely. We implement bank-level encryption, comply with GDPR, CCPA, and HIPAA (for healthcare), and follow SOC 2 Type II standards. All data is encrypted in transit and at rest. We provide detailed security documentation and can sign BAAs for healthcare clients."
    },
    {
      id: "6",
      question: "Can we customize the chatbot's personality and responses?",
      answer: "Yes! You have full control over the chatbot's tone, personality, and response style. We work with you to define brand voice guidelines, create custom response templates, and set conversation flows that match your customer service philosophy. The chatbot can be formal, friendly, or anything in between."
    },
    {
      id: "7",
      question: "What kind of analytics and reporting do you provide?",
      answer: "Our dashboard provides comprehensive insights including: conversation volume and trends, resolution rates, customer satisfaction scores, common questions and topics, response times, escalation patterns, and ROI metrics. You can export reports and integrate with your BI tools."
    },
    {
      id: "8",
      question: "Do you offer ongoing support and optimization?",
      answer: "Yes! All plans include ongoing support. We monitor performance, provide monthly optimization recommendations, update the AI training based on new patterns, and offer regular strategy calls. Our team is available via email, chat, and phone to ensure your chatbot continues to deliver value."
    }
  ];

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="HelpCircle" size={16} />
            <span>FAQ</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-text-secondary">
            Everything you need to know about AI chatbot automation
          </p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-card rounded-xl border border-border overflow-hidden transition-all duration-fast hover:shadow-md"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface transition-colors duration-fast"
                aria-expanded={openFAQ === faq.id}
              >
                <span className="text-lg font-semibold text-text-primary pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDown"
                  size={20}
                  className={`text-primary flex-shrink-0 transition-transform duration-fast ${
                    openFAQ === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              {openFAQ === faq.id && (
                <div className="px-6 pb-4 animate-fade-in">
                  <p className="text-text-secondary leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">
            Still have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" size="default" iconName="Mail" iconPosition="left">
              Email Support
            </Button>
            <Button variant="default" size="default" iconName="MessageCircle" iconPosition="left">
              Chat with Us
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;